import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import classes from "../css/Item.module.scss";
import ButtonSpinner from "../../common/ButtonSpinner";

const Item = (props) => {
  const [images, setImages] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState({});
  const [addingToCart, setAddingToCart] = useState(false);
  const inr = new Intl.NumberFormat("en-IN", { style: "decimal" });

  const {
    product,
    symbol,
    variantTag,
    initialVariantId,
    onAddToCartClicked,
    onAddToWishlistClicked,
    onRemoveFromWishlistClicked,
    onAddToCompareClicked,
    onRemoveFromCompareClicked,
    compareItems = [],
    wishListedItem = [],
    showCompare = true,
    wishlistAction = "toggle",
    onClick,
  } = props;
  const colorArray = useMemo(() => {
    const variants = product && product.variants ? product.variants : [];
    return [...new Map(variants.map((item) => [item.color, item])).values()];
  }, [product]);

  const isActive = useMemo(() => {
    if (
      !(selectedVariant && selectedVariant.id) ||
      !Array.isArray(wishListedItem)
    ) {
      return false;
    }
    return wishListedItem.some(
      (element) =>
        String(element && element.variant ? element.variant.id : undefined) ===
        String(selectedVariant.id),
    );
  }, [wishListedItem, selectedVariant]);

  const isCompareActive = useMemo(() => {
    if (
      !(selectedVariant && selectedVariant.id) ||
      !Array.isArray(compareItems)
    ) {
      return false;
    }
    return compareItems.some((element) => {
      const variantId =
        element && element.variant
          ? element.variant.id
          : element && element.selectedVariant
          ? element.selectedVariant.id
          : undefined;
      return String(variantId) === String(selectedVariant.id);
    });
  }, [compareItems, selectedVariant]);

  useEffect(() => {
    const variants = product && product.variants ? product.variants : [];
    if (!variants.length) {
      setSelectedVariant({});
      setImages([]);
      return;
    }

    let initialVariant = null;
    if (initialVariantId) {
      initialVariant = variants.find(
        (item) => String(item.id) === String(initialVariantId),
      );
    }
    if (!initialVariant && variantTag) {
      initialVariant = variants.find((item) => item.color === variantTag);
    }
    if (!initialVariant) {
      initialVariant = variants.find((item) => item.color === "rose-gold");
    }
    if (!initialVariant) {
      [initialVariant] = variants;
    }
    handleVariant(initialVariant);
  }, [product, variantTag, initialVariantId]);

  if (!product) {
    return null;
  }

  const buildPayload = (item) => ({
    ...item,
    selectedVariant,
  });

  const addToCartList = (item) => {
    if (!onAddToCartClicked || !(selectedVariant && selectedVariant.id)) {
      return;
    }
    setAddingToCart(true);
    const payload = buildPayload(item);
    onAddToCartClicked(payload, 1, selectedVariant);
    setTimeout(() => setAddingToCart(false), 1000);
  };

  const handleWishlist = (item) => {
    if (!(selectedVariant && selectedVariant.id)) {
      return;
    }
    const payload = buildPayload(item);

    if (isActive) {
      onRemoveFromWishlistClicked &&
        onRemoveFromWishlistClicked(payload, selectedVariant);
    } else {
      onAddToWishlistClicked &&
        onAddToWishlistClicked(payload, selectedVariant);
    }
  };

  const onAddToCompare = (item) => {
    if (!(selectedVariant && selectedVariant.id)) {
      return;
    }
    const payload = {
      ...buildPayload(item),
      variant: selectedVariant,
    };
    if (isCompareActive) {
      onRemoveFromCompareClicked && onRemoveFromCompareClicked(payload);
    } else {
      onAddToCompareClicked && onAddToCompareClicked(payload, selectedVariant);
    }
  };

  const removeFromWishlist = (item) => {
    if (!(selectedVariant && selectedVariant.id)) {
      return;
    }
    const payload = buildPayload(item);
    onRemoveFromWishlistClicked &&
      onRemoveFromWishlistClicked(payload, selectedVariant);
  };

  const handleMetalColor = (color) => {
    let cls = "";
    if (color === "yellow-gold") cls += classes.yellow_gold;
    else if (color === "rose-gold") cls += classes.rose_gold;
    else cls += classes.white_gold;
    if (color === selectedVariant.color) cls += " active";
    return cls;
  };

  const listHandle = (variant) => {
    handleVariant(variant);
  };

  const handleVariant = (variant) => {
    if (variant) {
      setImages((variant.image || "").split(","));
      setSelectedVariant(variant);
    }
  };

  const img_fallback = (event) => {
    event.target.parentNode.childNodes.forEach(function(node) {
      if (node.nodeName === "SOURCE") node.srcset = window.no_image;
      if (node.nodeName === "IMG") node.src = window.no_image;
    });
  };

  const formatUrlValue = (value) => {
    return value ? value.replace(/_/g, "-") : "";
  };

  const productUrl = `${process.env.PUBLIC_URL}/${formatUrlValue(
    product.value,
  )}?variant_id=${selectedVariant.id || ""}`;
  const primaryImage = images[0] || window.no_image;
  const secondaryImage = images[1] || images[0] || window.no_image;

  const saveScrollProduct = () => {
    onClick && onClick();
    try {
      sessionStorage.setItem("listing_scroll_product", product.objectID);
    } catch {}
  };

  return (
    <div
      key={props.index}
      className={classes.item}
      id={`product-${product.objectID}`}
    >
      <div className={classes.item_top}>
        <Link
          className="product-item-content"
          to={productUrl}
          onClick={saveScrollProduct}
        >
          <figure className={classes.image_wrap}>
            <img
              src={`${primaryImage}?resize=500,500&compress=90`}
              alt={product.name}
              onError={img_fallback}
              loading="lazy"
            />

            <img
              className={classes.hover_image}
              src={`${secondaryImage}?resize=500,500&compress=90`}
              alt={product.name}
              onError={img_fallback}
              loading="lazy"
            />
          </figure>
        </Link>
        {onAddToCartClicked && (
          <div className={classes.add_to_cart_btn_wrap}>
            <a
              className={classes.add_to_cart_btn}
              role="button" tabIndex={0}
              onClick={() => !addingToCart && addToCartList(product)}
            >
              {addingToCart ? <ButtonSpinner size={14} /> : "ADD TO CART"}
            </a>
          </div>
        )}
        <div className={classes.item_actions}>
          {wishlistAction === "remove" && onRemoveFromWishlistClicked ? (
            <a
              className={`${classes.wishlist} ${classes.remove_btn}`}
              role="button" tabIndex={0}
              onClick={() => {
                removeFromWishlist(product);
              }}
              aria-label="Remove from wishlist"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </a>
          ) : (
            (onAddToWishlistClicked || onRemoveFromWishlistClicked) && (
              <a
                className={classes.wishlist}
                role="button" tabIndex={0}
                aria-label="Wishlist"
                onClick={() => {
                  handleWishlist(product);
                }}
              >
                {isActive ? (
                  // Active Icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M19.4797 9.5184C19.4706 7.19386 18.251 5.00779 15.8467 4.23322C14.1957 3.70045 12.3973 3.99673 11.0006 6.00191C9.60394 3.99673 7.80555 3.70045 6.15456 4.23322C3.74992 5.00788 2.53035 7.19436 2.52153 9.5192C2.49929 14.1426 10.9994 19.3715 10.9994 19.3715L11.0006 19.371L11.0018 19.3715C11.0018 19.3715 19.5025 14.1422 19.4797 9.5184Z"
                      fill="#732257"
                      stroke="#732257"
                      strokeLinecap="square"
                    />
                  </svg>
                ) : (
                  // Default Icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M19.4797 9.5184C19.4706 7.19386 18.251 5.00779 15.8467 4.23322C14.1957 3.70045 12.3973 3.99673 11.0006 6.00191C9.60394 3.99673 7.80555 3.70045 6.15456 4.23322C3.74992 5.00788 2.53035 7.19436 2.52153 9.5192C2.49929 14.1426 10.9994 19.3715 10.9994 19.3715L11.0006 19.371L11.0018 19.3715C11.0018 19.3715 19.5025 14.1422 19.4797 9.5184Z"
                      stroke="black"
                      strokeLinecap="square"
                    />
                  </svg>
                )}
              </a>
            )
          )}
          {showCompare && (
            <a
              role="button" tabIndex={0}
              onClick={() => onAddToCompare(product)}
            >
              {isCompareActive ? (
                // Active Icon
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                >
                  <rect
                    x="2.80469"
                    y="3.16406"
                    width="5.64017"
                    height="5.64017"
                    fill="#732257"
                    stroke="#732257"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <rect
                    x="13.5586"
                    y="3.16406"
                    width="5.64017"
                    height="5.64017"
                    fill="#732257"
                    stroke="#732257"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="2.82422"
                    y1="11.5757"
                    x2="19.1787"
                    y2="11.5757"
                    stroke="#732257"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.0052 12.0757L14.3698 18.7236H7.64062L11.0052 12.0757Z"
                    fill="#732257"
                    stroke="#732257"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                // Default Icon
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                >
                  <rect
                    x="2.80469"
                    y="3.16406"
                    width="5.64017"
                    height="5.64017"
                    stroke="black"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <rect
                    x="13.5586"
                    y="3.16406"
                    width="5.64017"
                    height="5.64017"
                    stroke="black"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="2.82422"
                    y1="11.5757"
                    x2="19.1787"
                    y2="11.5757"
                    stroke="black"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.0052 12.0757L14.3698 18.7236H7.64062L11.0052 12.0757Z"
                    stroke="black"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </a>
          )}
        </div>
      </div>

      <div className={classes.item_content}>
        {colorArray && colorArray.length > 1 && (
          <div className={classes.color_wrap}>
            {colorArray && colorArray.length > 1 && (
              <div className={`${classes.color_button_wrap}  `}>
                {colorArray.map((vari, i) => {
                  return (
                    <button
                      key={i}
                      className={handleMetalColor(vari.color)}
                      onClick={() => listHandle(vari)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        <Link
          className={classes.bottom_link}
          to={productUrl}
          onClick={saveScrollProduct}
        >
          <h2 className={classes.title}> {product.name}</h2>
          <div className={classes.price_wrap}>
            {selectedVariant.offerprice ? (
              <div className={classes.offer_price}>
                {symbol} {inr.format(selectedVariant.offerprice)}
              </div>
            ) : (
              ""
            )}
          </div>
        </Link>
        {/* Add to Cart Button - Mobile  */}
        {onAddToCartClicked && (
          <div className={`${classes.add_to_cart_btn_mobile} d-xl-none`}>
            <button
              className={classes.add_to_cart_btn}
              onClick={() => addToCartList(product)}
              disabled={addingToCart}
            >
              {addingToCart ? <ButtonSpinner size={14} /> : "ADD TO CART"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Item;
